// 카리아카 사이드바 포크(216px · 포인트 박스 · 등록 안내 버튼 · 접이식 메뉴 그룹).
// 선생님/기관장 토글·서비스 플랜 카드·데이터랩 설정·권한 필터는 카리페이에 없어 뺐다.
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { CorpMenu, HOME_PATH } from "@/config/page_route";
import colors from "../../design/colors";
import fonts from "../../design/typography";
import { rootStore } from "../../mobx/store.js";
import { ChevronDown, ChevronRight } from "../icons/chevron";
import { observer } from "mobx-react";
import { getFilePath } from "@/utils/image";
import ImageCircle from "../image_circle";
import Button from "../button";
import { formatCommaNumber } from "@/utils/format";
import SettingsIcon from "../icons/settings";
import { Page_settings } from "@/config/page_settings";
import CariSymbol from "../icons/cariSymbol";

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
}

// containPath(":id" 포함)가 현재 경로와 맞는지
const matchesPath = (containPath: string[], currentPath: string) =>
  containPath.some((p) =>
    new RegExp(`^${p.replace(/:[^\s/]+/g, "[^/]+")}$`).test(currentPath)
  );

const Sidebar = observer(({ mobileOpen = false, onClose }: SidebarProps) => {
  const navigationRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!mobileOpen || !window.matchMedia("(max-width: 1000px)").matches) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const navigation = navigationRef.current;
    const focusable = () => Array.from(navigation?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex="0"]',
    ) ?? []).filter((element) => element.getClientRects().length > 0);
    focusable()[0]?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    navigation?.addEventListener("keydown", handleKeyDown);
    return () => {
      navigation?.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [mobileOpen, onClose]);
  /* ----------------------------------
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ---------------------------------- */
  const navigate = useNavigate();
  const corpId = rootStore.user_info?.corpId; // 사업장 ID
  const isBusinessRegistered = rootStore.user_info?.isBusinessRegistered; // 사업자 등록 완료 여부
  const currentPath = useLocation().pathname; // 현재 경로
  const corpInfo = rootStore.getCorpInfo; // 사업장 정보
  const context: any = useContext(Page_settings);

  /* ----------------------------------
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ---------------------------------- */
  // 발신번호 사전등록 여부 — 사업자 등록 완료 후 안내 버튼 노출 판단에 사용.
  // null = 미조회 / true = 신청 이력 있음 / false = 미신청
  const [hasSenderRegistration, setHasSenderRegistration] = useState<
    boolean | null
  >(null);

  // 포인트 박스 애니메이션 관련 state
  const [isPointBoxVisible, setIsPointBoxVisible] = useState<boolean>(() => {
    return !currentPath.startsWith("/point");
  });
  const [shouldRenderPointBox, setShouldRenderPointBox] = useState<boolean>(
    () => {
      return !currentPath.startsWith("/point");
    }
  );

  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isSettingsHovered, setIsSettingsHovered] = useState(false);

  /* ----------------------------------
   * 발신번호 사전등록 여부 조회 — 사업자 등록이 완료된 사업장만 대상.
   * 완료(신청 이력 존재)면 버튼을 숨기고, 미신청이면 "발신번호 등록하기" 버튼을 노출한다.
   ---------------------------------- */
  useEffect(() => {
    if (corpId == null || !isBusinessRegistered) {
      setHasSenderRegistration(null);
      return;
    }
    let cancelled = false;
    context.get(
      `/corp/${corpId}/sender-registration/me`,
      {},
      (res: any) => {
        if (cancelled) return;
        // result_data: null(미신청) → false, 레코드 존재 → true
        setHasSenderRegistration(res != null);
      },
      // 조회 실패 시 버튼을 띄우지 않음(노이즈 방지) — null 유지
      () => {
        if (!cancelled) setHasSenderRegistration(null);
      },
      false
    );
    return () => {
      cancelled = true;
    };
  }, [corpId, isBusinessRegistered]);

  // 메뉴 열림 상태
  const [isMenuOpen, setIsMenuOpen] = useState<boolean[]>(() =>
    Array(CorpMenu.length).fill(true)
  );

  const [hoverdIndex, setHoverdIndex] = useState<number[]>([-1, -1]); // 마우스 오버 상태

  /* ----------------------------------
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ---------------------------------- */
  const isSelected = (menu: (typeof CorpMenu)[number]) =>
    menu.children.some((child) => matchesPath(child.containPath, currentPath));

  const toggleMenu = (i: number) =>
    setIsMenuOpen(isMenuOpen.map((open, index) => (index === i ? !open : open)));

  /* ----------------------------------
   * EFFECTS
   * 컴포넌트 상태 변경 처리
   ---------------------------------- */
  // 경로 변경될 때 처리(포인트 화면에선 포인트 박스를 접는다)
  useEffect(() => {
    const isPointPage = currentPath.startsWith("/point");

    if (isPointPage) {
      setIsPointBoxVisible(false);
    } else {
      setShouldRenderPointBox(true);
      setTimeout(() => setIsPointBoxVisible(true), 100); // 트랜지션 유도
    }
  }, [currentPath]);

  // PointBox가 mount될 때 부드럽게 나타나도록 트랜지션 적용
  useEffect(() => {
    if (shouldRenderPointBox && !isPointBoxVisible) {
      setTimeout(() => {
        setIsPointBoxVisible(true);
      }, 10);
    }
  }, [shouldRenderPointBox]);

  // 트랜지션 종료 후 처리 (DOM 제거)
  const handlePointBoxTransitionEnd = () => {
    if (!isPointBoxVisible) {
      setShouldRenderPointBox(false);
    }
  };

  return (
    <>
    {mobileOpen && <MobileBackdrop data-testid="navigation-backdrop" onClick={onClose} aria-hidden="true" />}
    <Container ref={navigationRef} id="workspace-navigation" aria-label="카리페이 전체 메뉴" $mobileOpen={mobileOpen}>
      <MobileCloseButton type="button" aria-label="전체 메뉴 닫기" onClick={onClose}>✕</MobileCloseButton>
      <div style={{ marginBottom: 16, marginLeft: 4, flexShrink: 0 }}>
        <Link to={HOME_PATH} className="navbar-brand" aria-label="카리페이 홈">
          {!corpInfo ? (
            // 사이드바 내용 폭(151px)에 맞춘 크기 — 공식 비율 5.29:1
            <img src="/logo-pay.svg" alt="카리페이" width={127} height={24} />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                ...fonts.title4Bold,
                gap: 8,
                minHeight: 44,
              }}
            >
              {corpInfo?.thumbnail ? (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: `0.5px solid ${colors.gray100}`,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "content-box",
                  }}
                >
                  <ImageCircle
                    src={getFilePath(
                      corpInfo?.thumbnail,
                      "assets/image/default_corp.png"
                    )}
                    size={36}
                  />
                </div>
              ) : (
                <CariSymbol />
              )}
              <div
                style={{
                  marginLeft: 4,
                  minWidth: 0,
                  flex: 1,
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: colors.gray900,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minHeight: "1.5em",
                    maxHeight: "3em",
                    maxWidth: "100%",
                    ...fonts.title4Bold,
                    whiteSpace: "normal",
                  }}
                >
                  {corpInfo?.name}
                </span>
              </div>
            </div>
          )}
        </Link>
      </div>

      {/* 사업자/발신번호 등록 안내 버튼 */}
      {corpId != null && (
        <>
          {!isBusinessRegistered ? (
            <BusinessRegisterButton
              type="button"
              onClick={() => navigate("/business-registration/info")}
            >
              사업자 등록하기
            </BusinessRegisterButton>
          ) : (
            // 사업자 등록 완료 + 발신번호 미등록(me 조회 null)일 때만 발신번호 등록 안내.
            // 둘 다 완료(hasSenderRegistration === true)면 두 버튼 모두 숨김.
            hasSenderRegistration === false && (
              <BusinessRegisterButton
                type="button"
                onClick={() => navigate("/corp/sender-registration")}
              >
                발신번호 등록하기
              </BusinessRegisterButton>
            )
          )}
        </>
      )}

      {/* 포인트 박스 */}
      {corpId != null && shouldRenderPointBox && (
          <PointBox
            $visible={isPointBoxVisible}
            $animate={true}
            $buttonHovered={isButtonHovered || isSettingsHovered}
            onTransitionEnd={handlePointBoxTransitionEnd}
          >
            <PointBoxWrapper onClick={() => navigate("/point")}>
              <PointBoxTop>
                <span>카리페이 포인트</span>
                <div
                  className="settings-icon-container"
                  onMouseEnter={() => setIsSettingsHovered(true)}
                  onMouseLeave={() => setIsSettingsHovered(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.setPointSettingModalOpen(true);
                    navigate("/point");
                  }}
                >
                  <SettingsIcon
                    color={isSettingsHovered ? colors.gray600 : colors.gray300}
                  />
                </div>
              </PointBoxTop>
              <PointBoxTopPoint>
                <span>
                  {formatCommaNumber(rootStore.user_info?.pointBalance ?? 0)}P
                </span>
              </PointBoxTopPoint>
              <PointBoxBottom>
                <Button
                  size="small"
                  height="34px"
                  text="충전하기"
                  fontStyle={{ color: colors.blue600 }}
                  buttonColor={colors.blue50}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    rootStore.setPointModalOpen(true);
                    navigate("/point");
                  }}
                  onMouseEnter={() => setIsButtonHovered(true)}
                  onMouseLeave={() => setIsButtonHovered(false)}
                />
              </PointBoxBottom>
            </PointBoxWrapper>
          </PointBox>
        )}

      <SidebarMenuWrapper>
        {CorpMenu.map((menu, i) => (
            <React.Fragment key={i}>
              <SidebarMenuMain
                $isHovered={hoverdIndex[0] === i && hoverdIndex[1] === -1}
                isSelected={isSelected(menu)}
                onMouseEnter={() => setHoverdIndex([i, -1])}
                onMouseLeave={() => setHoverdIndex([-1, -1])}
                onClick={() => navigate(menu.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
              >
                <MenuDisclosure
                  type="button"
                  aria-label={isMenuOpen[i] ? "하위 메뉴 접기" : "하위 메뉴 펼치기"}
                  aria-expanded={isMenuOpen[i] ? "true" : "false"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu(i);
                  }}
                >
                  {isMenuOpen[i] ? (
                    <ChevronDown width="20px" height="20px" color={colors.gray200} />
                  ) : (
                    <ChevronRight width="20px" height="20px" color={colors.gray200} />
                  )}
                </MenuDisclosure>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {isSelected(menu) ? menu.iconActive : menu.iconInactive}
                  <div
                    className="menu-title"
                    style={{
                      ...fonts.body3SemiBold,
                      color: isSelected(menu) ? colors.blue600 : colors.gray500,
                      marginLeft: 8,
                    }}
                  >
                    {menu.title}
                  </div>
                </div>
              </SidebarMenuMain>
              {isMenuOpen[i] && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: 2,
                    marginBottom: 12,
                  }}
                >
                  {menu.children.map((child, j) => {
                    const selected = matchesPath(child.containPath, currentPath);
                    return (
                      <SidebarMenuSub
                        key={j}
                        type="button"
                        isHovered={hoverdIndex[0] === i && hoverdIndex[1] === j}
                        isSelected={selected}
                        onMouseEnter={() => setHoverdIndex([i, j])}
                        onMouseLeave={() => setHoverdIndex([-1, -1])}
                        onClick={() => navigate(child.path)}
                        style={{ cursor: "pointer" }}
                      >
                        <div
                          className="submenu-title"
                          style={{
                            ...(selected ? fonts.body3SemiBold : fonts.body3Medium),
                            color: selected ? colors.blue600 : colors.gray500,
                            marginLeft: 34,
                          }}
                        >
                          {child.title}
                        </div>
                      </SidebarMenuSub>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          ))}
      </SidebarMenuWrapper>
    </Container>
    </>
  );
});

export default Sidebar;

const Container = styled.nav<{ $mobileOpen: boolean }>`
  flex: 0 0 216px;
  width: 216px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  padding: 14px 14px 12px;
  border-right: 1px solid ${colors.gray100};
  height: 100%;
  overflow-y: auto;

  @media screen and (max-width: 1000px) {
    display: ${({ $mobileOpen }) => $mobileOpen ? "flex" : "none"};
    position: fixed;
    inset: 0 auto 0 0;
    width: min(280px, calc(100vw - 48px));
    height: 100dvh;
    z-index: 10001;
    box-shadow: 8px 0 32px rgba(27, 29, 31, 0.12);
  }
`;

const MobileBackdrop = styled.div`
  display: none;
  @media (max-width: 1000px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(27, 29, 31, 0.3);
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  @media (max-width: 1000px) {
    display: flex;
    align-self: flex-end;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin-bottom: 4px;
    border: 1px solid ${colors.gray100};
    border-radius: 10px;
    background: ${colors.white};
    color: ${colors.gray600};
    cursor: pointer;
  }
`;

const SidebarMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  flex: 1;
  min-height: 160px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 12px;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.12) transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.14);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const SidebarMenuMain = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ $isHovered?: boolean; isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  border-radius: 8px;
  padding: 0 8px;
  min-height: 36px;
  flex-shrink: 0;
  cursor: pointer;
  background-color: ${({ $isHovered }) =>
    $isHovered ? colors.gray50 : "transparent"};
  margin-bottom: 4px;
`;

const MenuDisclosure = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 32px;
  flex-shrink: 0;
  margin-right: 2px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
`;

const SidebarMenuSub = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ isHovered?: boolean; isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 34px;
  flex-shrink: 0;
  border: 0;
  padding: 0 8px 0 0;
  text-align: left;
  background-color: ${({ isHovered, isSelected }) =>
    isSelected ? colors.blue50 : isHovered ? colors.gray50 : colors.white};
  border-radius: 10px;
  transition: background-color 0.15s ease;

`;

const BusinessRegisterButton = styled.button`
  width: 100%;
  height: 40px;
  margin-bottom: 12px;
  border: 1px solid ${colors.blue100};
  border-radius: 12px;
  background: ${colors.blue50};
  color: ${colors.blue600};
  font-family: ${fonts.label4SemiBold.fontFamily};
  font-size: ${fonts.label4SemiBold.fontSize};
  line-height: ${fonts.label4SemiBold.lineHeight};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  box-sizing: border-box;
  flex-shrink: 0;

  &:hover {
    background: ${colors.blue100};
  }
`;

// 포인트
const PointBox = styled.div<{
  $visible: boolean;
  $animate: boolean;
  $buttonHovered: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  background-color: ${({ $buttonHovered }) =>
    $buttonHovered ? colors.white : colors.white};
  border-radius: 16px;
  padding: ${({ $visible }) => ($visible ? "12px" : "0 12px")};
  border: 1px solid ${colors.gray100};
  margin-bottom: ${({ $visible }) => ($visible ? "12px" : "0")};
  transition: ${({ $animate }) =>
    $animate
      ? "opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease, padding 0.3s ease, margin-bottom 0.3s ease, background-color 0.2s ease"
      : "none"};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
    $visible ? "translateY(0px)" : "translateY(-10px)"};
  max-height: ${({ $visible }) => ($visible ? "300px" : "0px")};
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  &:hover {
    background-color: ${({ $buttonHovered }) =>
      $buttonHovered ? colors.white : colors.gray50};
  }
`;

const PointBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  cursor: pointer;
  &:hover {
    background-color: transparent;
  }
`;

const PointBoxTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  position: relative;

  span:first-child {
    font-family: ${fonts.label5Medium.fontFamily};
    font-size: ${fonts.label5Medium.fontSize};
    line-height: ${fonts.label5Medium.lineHeight};
    color: ${colors.gray500};
  }
`;

const PointBoxTopPoint = styled.div`
  font-family: ${fonts.label2SemiBold.fontFamily};
  font-size: ${fonts.label2SemiBold.fontSize};
  line-height: ${fonts.label2SemiBold.lineHeight};
  color: ${colors.gray900};
  font-weight: 700;
  margin-bottom: 12px;
`;

const PointBoxBottom = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  font-family: ${fonts.label4Medium.fontFamily};
  font-size: ${fonts.label4Medium.fontSize};
  line-height: ${fonts.label4Medium.lineHeight};

  button {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${colors.blue100};
    }
  }
`;

